import React, { useState } from "react";

import { Add as AddIcon, Warning as WarningIcon } from "@mui/icons-material";
import {
  Box,
  Chip,
  IconButton,
  ListItem,
  ListItemAvatar,
  ListItemText,
  Portal,
  Tooltip,
  Typography,
} from "@mui/material";
import { intersection } from "lodash";
import { Highlight } from "react-instantsearch";
import { makeStyles, withStyles } from "tss-react/mui";

import { graphql } from "@/gql";
import {
  AllergiesKind,
  FoodAddPortionDetailsFragment,
  FoodSource,
} from "@/gql/graphql";
import { colors } from "@/theme/overrides/colors";
import { percent, round } from "@/utils/number";
import { FragmentType } from "@/utils/type";

import AddFoodPortionDialog from "./AddFoodPortionDialog";
import FoodItemIcon from "./FoodItemIcon";
import AllergenIngredientModal from "../AllergenModal/AllergenIngredientModal";
import AllergenRecipesModal from "../AllergenModal/AllergenRecipesModal";
import InfoIcon from "../Icons/InfoIcon";

interface Props {
  hit: any;
  onAddFood: (
    food: FragmentType<typeof foodItemFragment>,
    foodPortion: FoodAddPortionDetailsFragment,
    amount: number
  ) => void;
  clientAllergies?: AllergiesKind[];
}

const useStyles = makeStyles()((theme) => ({
  foodIcon: {
    color: colors.primary,
    backgroundColor: "rgba(31,212,175,0.1)", // #1FD4AF, 10% opacity
  },
  fruitIcon: {
    color: colors.secondary,
    backgroundColor: "rgba(255,157,55,0.1)", // #FF9D37, 10% opacity
  },
  infoIcon: {
    marginLeft: theme.spacing(2),
    fontSize: 16,
    color: "#C0C5CD",
  },
  addButton: {
    padding: theme.spacing(2),
    color: colors.gray,
    backgroundColor: "#E8EEF6",
  },
  textPrimary: {
    fontSize: 16,
    lineHeight: "22px",
    fontWeight: 600,
    color: colors.dark,
  },
  textSecondary: {
    display: "flex",
    alignItems: "center",
    fontSize: 14,
    lineHeight: "20px",
    fontWeight: 600,
    color: colors.gray,
  },
  tooltipArrow: {
    color: colors.dark,
  },
  tooltipTooltip: {
    backgroundColor: colors.dark,
    borderRadius: 0,
  },
  tooltipText: {
    fontSize: 12,
    lineHeight: "14px",
    fontWeight: 600,
    color: colors.white,
  },
  tooltipTextValue: {
    color: theme.palette.primary.main,
  },
  epWeight: {
    background: theme.palette.grey["200"],
    borderRadius: "4px",
    padding: "2px 8px",
    marginLeft: theme.spacing(2),
  },
}));

const StyledChip = withStyles(Chip, (theme) => ({
  root: {
    backgroundColor: "#FF6262",
    color: colors.white,
    maxHeight: 21,
    fontSize: theme.typography.pxToRem(10),
  },
}));

const StyledWarningIcon = withStyles(WarningIcon, {
  root: {
    maxHeight: 12,
    maxWidth: 12,
  },
});

const foodItemFragment = graphql(`
  fragment FoodItem on Food {
    id
    source
    sourceId
    category
    name
    epWeightGrams
    energyKcal
    carbohydrateGrams
    proteinGrams
    fatGrams
    portions {
      ...FoodAddPortionDetails
    }
    allergies
  }
`);

const FoodItemHit: React.FC<Props> = ({
  hit,
  onAddFood,
  clientAllergies = [],
}) => {
  const { classes } = useStyles();
  const [portionDialogOpen, setPortionDialogOpen] = useState(false);
  const [allergenRecipesModal, setAllergenRecipesModal] = useState(false);
  const [allergenIngredientModal, setAllergenIngredientModal] = useState(false);

  const hasAllergies =
    intersection(hit.allergies, clientAllergies).length !== 0;

  const handleAdd = () => {
    if (hasAllergies) {
      if (hit.source == FoodSource.Recipes.toLocaleLowerCase()) {
        setAllergenRecipesModal(true);
      } else {
        setAllergenIngredientModal(true);
      }
    } else {
      setPortionDialogOpen(true);
    }
  };

  const macronutrientValue = (macro: number | null | undefined) => {
    if (macro == null) return "N/A";
    const percentage = percent(macro / hit.ep_weight_grams, { round: 0 });
    return `${round(macro, 2)}g(${percentage}%)`;
  };

  return (
    <ListItem
      disableGutters
      secondaryAction={
        <IconButton
          className={classes.addButton}
          aria-label="add"
          onClick={handleAdd}
          size="large"
        >
          <AddIcon />
        </IconButton>
      }
    >
      <ListItemAvatar>
        <FoodItemIcon category={hit.category} />
      </ListItemAvatar>
      <ListItemText
        primary={
          <Box display="flex" justifyContent="space-between" marginRight={4}>
            <Typography variant="body1" color="textPrimary">
              <Highlight attribute="name" hit={hit} />
            </Typography>
            {hasAllergies && (
              <StyledChip
                icon={<StyledWarningIcon style={{ color: colors.white }} />}
                label="Allergen"
              />
            )}
          </Box>
        }
        secondary={
          <>
            <span>{Math.round(hit.energy_kcal)} kcal</span>
            <span className={classes.epWeight}>
              {round(hit.ep_weight_grams, 2)} g
            </span>
            <Tooltip
              arrow
              classes={{
                arrow: classes.tooltipArrow,
                tooltip: classes.tooltipTooltip,
              }}
              title={
                <div>
                  <Typography className={classes.tooltipText}>
                    Proteins :{" "}
                    <span className={classes.tooltipTextValue}>
                      {macronutrientValue(hit.protein_grams)}
                    </span>
                    , Fats :{" "}
                    <span className={classes.tooltipTextValue}>
                      {macronutrientValue(hit.fat_grams)}
                    </span>
                    ,<br /> Carbohydrates :{" "}
                    <span className={classes.tooltipTextValue}>
                      {macronutrientValue(hit.carbohydrate_grams)}
                    </span>
                  </Typography>
                  <br />
                  <Typography className={classes.tooltipText}>
                    Source: {hit.source}
                  </Typography>
                </div>
              }
            >
              <InfoIcon className={classes.infoIcon} />
            </Tooltip>
          </>
        }
        classes={{
          primary: classes.textPrimary,
          secondary: classes.textSecondary,
        }}
      />
      <Portal>
        <AllergenRecipesModal
          foodId={hit.id}
          visibility={allergenRecipesModal}
          onAddRecipe={() => {
            setAllergenRecipesModal(false);
            setPortionDialogOpen(true);
          }}
          onClose={() => setAllergenRecipesModal(false)}
        />
        <AllergenIngredientModal
          foodId={hit.id}
          visibility={allergenIngredientModal}
          onAddIngredient={() => {
            setAllergenIngredientModal(false);
            setPortionDialogOpen(true);
          }}
          onClose={() => setAllergenIngredientModal(false)}
        />
        <AddFoodPortionDialog
          foodId={hit.id}
          open={portionDialogOpen}
          onClose={() => setPortionDialogOpen(false)}
          onAddFood={onAddFood}
        />
      </Portal>
    </ListItem>
  );
};

export default FoodItemHit;
